import { FindingChangesPanel } from "../finding/changes/FindingChangesPanel";
import { ReviewAssessmentCard } from "../ReviewDetail";
import { Button } from "../../ui/button";
import type { ReactNode } from "react";
import type { Recommendation } from "../../../state/governance";
import { formatDate } from "./PolicyGovernanceTabs";
import { suggestionChangeRows } from "./SuggestionReview.logic";

export function SuggestionReview({
  suggestion,
  comments,
  onViewChanges,
}: {
  suggestion: Recommendation;
  comments: ReactNode;
  onViewChanges: () => void;
}) {
  const changes = suggestionChangeRows(suggestion);
  return (
    <div className="space-y-6">
      <ReviewAssessmentCard>
        <div>
          <h2 className="font-semibold">What needs attention</h2>
          <p className="mt-2 whitespace-pre-line break-words text-muted-foreground">
            {suggestion.rationale}
          </p>
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold">Required changes</h3>
            <Button size="xs" variant="ghost" onClick={onViewChanges}>
              View changes
            </Button>
          </div>
          {changes.length > 0 ? (
            <ul className="mt-2 list-disc space-y-2 ps-5">
              {changes.map((change) => (
                <li className="break-words whitespace-pre-line" key={change.key}>
                  {/* Prose stands on its own; a value gets its field name. */}
                  {change.key === "amendment" || change.key === "contextAddition"
                    ? change.value
                    : `${change.label}: ${change.value}`}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 break-words">
              {suggestion.kind === "missing_document"
                ? suggestion.title
                : "Review the rationale above before deciding."}
            </p>
          )}
        </div>
      </ReviewAssessmentCard>
      {suggestion.decidedAt || suggestion.decisionNote ? (
        <section className="border-t border-border/60 pt-5 text-sm leading-6">
          <h2 className="font-semibold">
            Latest decision{suggestion.decidedByEmail ? ` by ${suggestion.decidedByEmail}` : ""}
          </h2>
          {suggestion.decidedAt ? (
            <time dateTime={suggestion.decidedAt} className="text-xs text-muted-foreground">
              {formatDate(suggestion.decidedAt)}
            </time>
          ) : null}
          {suggestion.decisionNote ? (
            <p className="mt-2 whitespace-pre-wrap break-words text-muted-foreground">
              {suggestion.decisionNote}
            </p>
          ) : null}
          {suggestion.resolvedVersion != null ? (
            <p>Resolved in version {suggestion.resolvedVersion}.</p>
          ) : null}
          {suggestion.resolvedPolicyName ? (
            <p>Resolved with {suggestion.resolvedPolicyName}.</p>
          ) : null}
        </section>
      ) : null}
      {comments}
    </div>
  );
}

export function SuggestionChanges({
  suggestion,
  organizationId,
  currentVersion,
  missingDocumentAction,
}: {
  suggestion: Recommendation;
  organizationId: string;
  currentVersion: number | null;
  /** Offered under a missing document's description: drafting it from a template. */
  missingDocumentAction?: ReactNode;
}) {
  const changes = suggestionChangeRows(suggestion);
  return (
    <>
      {suggestion.kind === "control_amendment" && suggestion.policyId && organizationId ? (
        <section aria-label="Proposed changes">
          <FindingChangesPanel
            organizationId={organizationId}
            policyId={suggestion.policyId}
            findingId={suggestion.id}
            reviewKind="recommendation"
            currentVersion={currentVersion}
            requiredChanges={changes.map((change) =>
              change.key === "amendment" ? change.value : `${change.label}: ${change.value}`,
            )}
          />
        </section>
      ) : (
        <section
          aria-label="Proposed changes"
          className="space-y-4 rounded-xl border border-border/60 bg-card p-5 sm:p-6"
        >
          <h2 className="text-sm font-semibold">Proposed changes</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            The changes recorded in this suggestion.
          </p>
          {changes.length > 0 ? (
            <dl className="mt-5 divide-y divide-border/60 border-y border-border/60">
              {changes.map((change) => (
                <div
                  key={change.key}
                  className="grid gap-2 py-4 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-6"
                >
                  <dt className="text-xs font-medium text-muted-foreground">{change.label}</dt>
                  <dd className="whitespace-pre-wrap break-words text-sm leading-7">
                    {change.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="space-y-3 text-sm leading-6">
              <h3 className="font-semibold">
                {suggestion.kind === "missing_document"
                  ? suggestion.title
                  : "No specific wording recorded"}
              </h3>
              <p className="whitespace-pre-wrap break-words text-muted-foreground">
                {suggestion.rationale}
              </p>
              {suggestion.kind === "missing_document" ? (
                <>
                  <p className="text-muted-foreground">
                    Use Resolve to upload the missing document or select one added since this
                    suggestion was raised.
                    {missingDocumentAction
                      ? " To write it first, start from a template: eve drafts it from your organisation's details, and you resolve this against the document it files."
                      : null}
                  </p>
                  {missingDocumentAction ? <div>{missingDocumentAction}</div> : null}
                </>
              ) : null}
            </div>
          )}
        </section>
      )}
    </>
  );
}
